import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../../../components/ui/Card';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { STATUS_OPTIONS } from '../utils/linkTokenFormat';

const EXPIRING_OPTIONS = [
  { value: '', label: 'Any expiry' },
  { value: '3', label: 'Expiring ≤ 3 days' },
  { value: '7', label: 'Expiring ≤ 7 days' },
  { value: '14', label: 'Expiring ≤ 14 days' },
  { value: '30', label: 'Expiring ≤ 30 days' },
];

// "<field>:<direction>" — collapsed into one control because "Newest first"
// reads better to staff than a field picker plus an asc/desc toggle.
const SORT_OPTIONS = [
  { value: 'issued_at:desc', label: 'Newest first' },
  { value: 'issued_at:asc', label: 'Oldest first' },
  { value: 'expires_at:asc', label: 'Expiring soonest' },
  { value: 'expires_at:desc', label: 'Expiring latest' },
];

const LinkTokenFilters = ({ filters, onChange, onClear, hasActiveFilters }) => {
  // Local buffer for the search box so we can debounce server-side q.
  const [searchInput, setSearchInput] = useState(filters.q);

  // Keep local input in sync when filters change externally (e.g. Clear / back).
  useEffect(() => {
    setSearchInput(filters.q);
  }, [filters.q]);

  // Debounce the server-side q param.
  useEffect(() => {
    if (searchInput === filters.q) return undefined;
    const t = setTimeout(() => {
      onChange({ q: searchInput });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  return (
    <Card className="border border-slate-200 shadow-sm bg-white">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Server-side search */}
          <div className="flex-1 min-w-[220px]">
            <div className="relative">
              <Icon
                name="Search"
                size={18}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by recipient email or vendor name..."
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Stage is selected via the tab bar above (StageTabs). */}

          {/* Status multi-select */}
          <div className="min-w-[180px]">
            <Select
              multiple
              options={STATUS_OPTIONS}
              value={filters.status}
              onChange={(value) => onChange({ status: value })}
              placeholder="All statuses"
            />
          </div>

          {/* Expiring within N days */}
          <div className="min-w-[170px]">
            <select
              value={filters.expiring_within_days ? String(filters.expiring_within_days) : ''}
              onChange={(e) =>
                onChange({
                  expiring_within_days: e.target.value ? parseInt(e.target.value, 10) : undefined,
                })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 bg-white"
            >
              {EXPIRING_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Sort — one control for both the field and the direction, since
              "Newest first" reads better to staff than a field + asc/desc pair. */}
          <div className="min-w-[190px]">
            <select
              value={`${filters.sort}:${filters.order}`}
              onChange={(e) => {
                const [sort, order] = e.target.value.split(':');
                onChange({ sort, order });
              }}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 bg-white"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Expired, not yet regenerated */}
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={filters.expired_unactioned}
              onChange={(e) => onChange({ expired_unactioned: e.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            Expired, not yet regenerated
          </label>

          {/* Clear */}
          {hasActiveFilters && (
            <button
              onClick={onClear}
              className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 transition-colors"
            >
              <Icon name="X" size={14} />
              Clear
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default LinkTokenFilters;
